import {
  Button,
  Table,
  Spinner,
  ToastContainer,
  Toast,
  Modal,
  Popover,
  CloseButton,
  OverlayTrigger,
  Badge,
} from "react-bootstrap";
import { useContext, useEffect, useRef, useState } from "react";
import { Course } from "../../entities/course";
import CourseForm from "./CourseForm";
import CourseListItem from "./CourseListItem";
import { Teacher } from "../../entities/teacher";
import { Semester } from "../../entities/semester";
import { Stg } from "../../entities/stg";
import {
  createRemoteCourse,
  createRemoteTeams,
  getRemoteCoursesForCurrentUser,
  getRemoteOldCourseTemplatesForCurrentUser,
  getRemoteSemesters,
  getRemoteStgs,
  getRemoteTeachers,
  getRemoteTeachersForCurrentUserCourses,
  getTeamsMemberQueue,
  TeamsMemberQueueResponse,
} from "../../services/api";
import { UserContext } from "../../userContext";
import CourseFilter from "./CourseFilter";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { useInterval } from "usehooks-ts";

export default function Courses() {
  const { t, i18n } = useTranslation("common");
  const user = useContext(UserContext);

  const [show, setShow] = useState(false);
  const [currentCourse, setCurrentCourse] = useState<Course>();
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [courseRequestLoading, setCourseRequestLoading] =
    useState<boolean>(true);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [courseTeachers, setCourseTeachers] = useState<any[]>([]);
  const [semesters, setSemesters] = useState<Semester[]>([]);
  const [stgs, setStgs] = useState<Stg[]>([]);

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [showTeamsLoading, setShowTeamsLoading] = useState<boolean>(false);
  const [syncData, setSyncData] = useState<TeamsMemberQueueResponse | null>(null);
  const [loadingSyncData, setLoadingSyncData] = useState(false);
  const [showLongLoading, setShowLongLoading] = useState<boolean>(false);

  const [showImportInfo, setShowImportInfo] = useState<boolean>(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCourses, setTotalCourses] = useState<number>(0);
  const [limit] = useState<number>(50);

  const emptyFilters = { teacher: "", title: "" };
  const [filterValues, setFilterValues] = useState<any>(emptyFilters);
  const isInitialMount = useRef(true);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useInterval(
    () => {
      // Your custom logic here
      setShowLongLoading(true);
    },
    // Delay in milliseconds or null to stop it
    courseRequestLoading ? 2000 : null
  );

  const getCourses = async (page: number = currentPage) => {
    setCourseRequestLoading(true);
    const response = await getRemoteCoursesForCurrentUser(
      page,
      limit,
      filterValues.title || undefined,
      filterValues.teacher || undefined
    );

    if (response.data) {
      setAllCourses(response.data);
      setTotalCourses(response.pagination.total);
      setTotalPages(response.pagination.pages);
      setCurrentPage(response.pagination.page);
    }

    setCourseRequestLoading(false);
    setShowLongLoading(false);

    // Sync data nur für Admins laden
    if (user?.role === "admin") {
      fetchSyncData();
    }
  };

  const fetchSyncData = async () => {
    try {
      setLoadingSyncData(true);
      const syncStatusData = await getTeamsMemberQueue({});
      setSyncData(syncStatusData);
    } catch (err) {
      console.error('Error fetching sync data:', err);
      // Error loading sync status is not critical
    } finally {
      setLoadingSyncData(false);
    }
  };

  const getTeamSyncStatus = (teamId: string | null) => {
    if (loadingSyncData) return {
      mainStatus: { status: t('syncStatus.loading'), variant: 'secondary', details: null },
      errorStatus: null
    };
    if (!syncData) return {
      mainStatus: { status: t('syncStatus.notAvailable'), variant: 'secondary', details: null },
      errorStatus: null
    };

    if (!teamId) {
      return {
        mainStatus: { status: t('syncStatus.notAvailable'), variant: 'secondary', details: null },
        errorStatus: null
      };
    }

    // Find the team in sync data by team_id (viel robuster als team_name!)
    const teamSyncInfo = syncData.teams.find(team => team.team_id === teamId);

    if (!teamSyncInfo) {
      return {
        mainStatus: { status: t('syncStatus.notSynchronized'), variant: 'secondary', details: null },
        errorStatus: null
      };
    }
    
    // API gibt Strings zurück - zu Numbers konvertieren
    const total_members = parseInt(teamSyncInfo.total_members.toString());
    const completed = parseInt(teamSyncInfo.completed.toString());
    const pending = parseInt(teamSyncInfo.pending.toString());
    const failed = parseInt(teamSyncInfo.failed.toString());
    
    // Processing berechnen: total_members - completed - pending - failed
    const processing = Math.max(0, total_members - completed - pending - failed);
    
    // Hauptstatus basierend auf aktueller Aktivität (processing > pending > completed)
    let mainStatus;
    
    if (processing > 0) {
      mainStatus = {
        status: t('syncStatus.synchronizing'),
        variant: 'info',
        details: pending > 0
          ? t('syncStatus.detailsProcessingWithPending', { completed, total_members, processing, pending })
          : t('syncStatus.detailsProcessing', { completed, total_members, processing })
      };
    } else if (pending > 0) {
      mainStatus = {
        status: t('syncStatus.waitingForSync'),
        variant: 'warning',
        details: t('syncStatus.detailsPending', { completed, total_members, pending })
      };
    } else if (completed === total_members && total_members > 0) {
      mainStatus = {
        status: t('syncStatus.fullySynchronized'),
        variant: 'success',
        details: t('syncStatus.detailsSuccess', { completed, total_members })
      };
    } else if (completed > 0) {
      mainStatus = {
        status: t('syncStatus.partiallySynchronized'),
        variant: 'info',
        details: t('syncStatus.detailsSuccess', { completed, total_members })
      };
    } else {
      mainStatus = { status: t('syncStatus.starting'), variant: 'secondary', details: null };
    }
    
    // Fehler-Status als separater Badge
    const errorStatus = failed > 0 ? {
      status: t('syncStatus.errorCount', { count: failed }),
      variant: 'danger',
      details: t('syncStatus.failedSynchronizations', { count: failed })
    } : null;
    
    return { mainStatus, errorStatus };
  };
  const getTeachers = async () => {
    const data = await getRemoteTeachers();
    setTeachers(data);
  };
  const getSemesters = async () => {
    const data = await getRemoteSemesters();
    setSemesters(data);
  };
  const getStgs = async () => {
    const data = await getRemoteStgs();
    setStgs(data);
  };
  const getCourseTeachers = async () => {
    const data = await getRemoteTeachersForCurrentUserCourses();
    setCourseTeachers(data);
  };

  // Courses kommen jetzt gefiltert vom Backend
  const courses = allCourses;

  const createCourses = async (course: Course) => {
    const formData = new FormData();
    Object.keys(course).forEach((key: string) => {
      const param = course[key as keyof Course];
      if (
        key == "teacher" ||
        key == "coTeachers" ||
        key == "nonEditingTeachers"
      ) {
        formData.append(key, JSON.stringify(param));
      } else if (param) {
        formData.append(key, param.toString());
      } else {
        formData.append(key, "");
      }
    });
    createRemoteCourse(formData);
  };

  const handleCreateTeam = async (course: any) => {
    const formData = new FormData();
    formData.append("courseTitle", course.titleGerman); //TODO translate
    formData.append("shortSemester", course.wsSs + course.year.substring(2));
    formData.append("shortStg", course.stg);
    if (course.id) {
      formData.append("fhsysCourseId", course.id);
    }
    formData.append("teachers", "[]");
    setShowTeamsLoading(true);
    await createRemoteTeams(formData);
    setShowTeamsLoading(false);
    setAllCourses([]);
    getCourses();
    // Sync-Daten nach Team-Erstellung auch aktualisieren (nur für Admins)
    if (user?.role === "admin") {
      setTimeout(fetchSyncData, 2000); // Kurze Verzögerung, damit API Zeit hat
    }
  };

  const handleSubmit = async (newCourse: Course) => {
    setSubmitLoading(true);
    await createCourses(newCourse);
    setAllCourses([]);
    setSubmitLoading(false);
    getCourses();
    handleClose();
    if (newCourse.templateOldCourseId) {
      setShowImportInfo(true);
    }
  };

  const handleReloadCourses = () => {
    setAllCourses([]);
    getCourses();
  };

  useEffect(() => {
    // Alle Requests parallel starten
    Promise.all([
      getTeachers(),
      getSemesters(),
      getStgs(),
      getCourseTeachers(),
      getCourses(1) // Initial load
    ]);

    // Cleanup: Reset flag for StrictMode double-mount
    return () => {
      isInitialMount.current = true;
    };
  }, []); // Nur beim Mount

  // Lade Kurse bei Filter-Änderungen (aber NICHT beim initialen Mount!)
  useEffect(() => {
    // Skip initial render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    getCourses(1); // Zurück zu Seite 1 bei Filter-Änderung
  }, [filterValues]);

  const listItems = courses.map((course) => (
    <CourseListItem
      key={course.id}
      course={course}
      setCurrentCourse={setCurrentCourse}
      handleShow={handleShow}
      handleCreateTeam={handleCreateTeam}
      getTeamSyncStatus={getTeamSyncStatus}
      handleReloadCourses={handleReloadCourses}
    />
  ));
  const showInstructor = user?.departments.length || user?.fachbereiche.length;

  const teacherInfoBox = (
    <Popover id="popover-basic">
      <Popover.Header className="d-flex justify-content-between align-items-center">
        <div className="h5" style={{ marginTop: "0.1rem", marginBottom: "0" }}>
          Info
        </div>
      </Popover.Header>
      <Popover.Body>{t("courses.teacherHeaderInfo")}</Popover.Body>
    </Popover>
  );

  const statusInfoBox = (
    <Popover id="popover-basic">
      <Popover.Header className="d-flex justify-content-between align-items-center">
        <div className="h5" style={{ marginTop: "0.1rem", marginBottom: "0" }}>
          Info
        </div>
      </Popover.Header>
      <Popover.Body>
        <div className="mb-2">
          <Badge bg="warning">{t("moodleCourseStatus.inProgress")}</Badge>{" "}
          <br />
          {t("moodleCourseStatus.inProgressInfo")}
        </div>
        <div className="mb-2">
          <Badge bg="info">{t("moodleCourseStatus.importInProgress")}</Badge>{" "}
          <br />
          {t("moodleCourseStatus.importInProgressInfo")}
        </div>
        <div>
          <Badge bg="success">{t("moodleCourseStatus.created")}</Badge> <br />
          {t("moodleCourseStatus.createdInfo")}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <div>
      {showTeamsLoading && (
        <div
          className="fullMask"
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            background: "black",
            opacity: "0.7",
            zIndex: "1",
          }}
        ></div>
      )}
      <h2>{t("menu.curriclarTitle")}</h2>
      <p style={{ maxWidth: "800px" }}>{t("courses.curricularDescription")}</p>
      <CourseFilter
        showInstructor={showInstructor}
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        teachers={courseTeachers}
      />

      {/* Zeige Tabelle nur wenn nicht am Laden */}
      {!courseRequestLoading && (
        <>
          <div className="table-responsive">
            <Table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("courses.departmentHeader")}</th>
                  <th scope="col">{t("courses.stgHeader")}</th>
                  <th scope="col">{t("courses.fachbereichHeader")}</th>
                  <th scope="col">{t("courses.semesterHeader")}</th>
                  {!!showInstructor && (
                    <th scope="col">
                      {t("courses.teacherHeader")} &nbsp;
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="right"
                        overlay={teacherInfoBox}
                      >
                        <a href="" onClick={(e) => e.preventDefault()}>
                          <FiInfo />
                        </a>
                      </OverlayTrigger>
                    </th>
                  )}
                  <th scope="col">{t("courses.lvNumberHeader")}</th>
                  <th scope="col">{t("courses.titleHeader")}</th>
                  <th scope="col">{t("courses.typeHeader")}</th>
                  <th scope="col">
                    {t("courses.moodleHeader")} &nbsp;
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      overlay={statusInfoBox}
                    >
                      <a href="" onClick={(e) => e.preventDefault()}>
                        <FiInfo />
                      </a>
                    </OverlayTrigger>
                  </th>
                  <th scope="col">{t("courses.teamsHeader")}</th>
                </tr>
              </thead>
              <tbody>{listItems}</tbody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {courses.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
          <div>
            Zeige {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, totalCourses)} von {totalCourses} Kursen
          </div>
          <nav aria-label="Course pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => getCourses(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Zurück
                </button>
              </li>

              {/* Erste Seite */}
              {currentPage > 2 && (
                <>
                  <li className="page-item">
                    <button className="page-link" onClick={() => getCourses(1)}>1</button>
                  </li>
                  {currentPage > 3 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                </>
              )}

              {/* Vorherige Seite */}
              {currentPage > 1 && (
                <li className="page-item">
                  <button className="page-link" onClick={() => getCourses(currentPage - 1)}>
                    {currentPage - 1}
                  </button>
                </li>
              )}

              {/* Aktuelle Seite */}
              <li className="page-item active">
                <span className="page-link">{currentPage}</span>
              </li>

              {/* Nächste Seite */}
              {currentPage < totalPages && (
                <li className="page-item">
                  <button className="page-link" onClick={() => getCourses(currentPage + 1)}>
                    {currentPage + 1}
                  </button>
                </li>
              )}

              {/* Letzte Seite */}
              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                  <li className="page-item">
                    <button className="page-link" onClick={() => getCourses(totalPages)}>
                      {totalPages}
                    </button>
                  </li>
                </>
              )}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => getCourses(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Weiter
                </button>
              </li>
            </ul>
          </nav>
        </div>
          )}
        </>
      )}

      {courseRequestLoading && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div>
            <Spinner animation="border" role="status" />
          </div>
        </div>
      )}
      {!courseRequestLoading && courses.length <= 0 && (
        <div>{t("courses.noCoursesFound")}</div>
      )}
      {currentCourse && currentCourse.curricular && (
        <CourseForm
          key={currentCourse.id}
          currentCourse={currentCourse}
          show={show}
          handleClose={handleClose}
          submitLoading={submitLoading}
          handleSubmit={handleSubmit}
          teachers={teachers}
          semesters={semesters}
          stgs={stgs}
        />
      )}
      <Modal
        show={showTeamsLoading}
        onHide={() => {
          setShowTeamsLoading(false);
        }}
      >
        <Modal.Header>
          <Modal.Title>Teams</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>{t("courses.teamsCourseCreationInProgress")}</div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showImportInfo}
        onHide={() => {
          setShowImportInfo(false);
        }}
      >
        <Modal.Header closeButton>{t("courses.templateImport")}</Modal.Header>
        <Modal.Body>{t("courses.templateImportInProgressInfo")}</Modal.Body>
      </Modal>
    </div>
  );
}
